// Phase JA.4 — Slip Builder pure helpers.
//
// Doctrine carry-forward:
//   - No stake recommendation. We compute the combined odds and a
//     potential-return only when the user has typed their own stake.
//     We never default the stake to a value, and we never recommend one.
//   - No fabrication. Empty draft yields combinedOdds=1 (multiplicative
//     identity) but the UI suppresses leg-count/return display until at
//     least one leg is added.

import type { DailyAccaSignal } from "../../free-tier/useDailyAccaSignals.ts";
import type { UserSlipLeg } from "../../free-tier/useUserSlips.ts";

export const DRAFT_SLIP_KEY = "daily_acca_draft_slip";

export interface DraftSlip {
  legs: UserSlipLeg[];
  /** User-typed stake input (string preserves "" empty state). */
  stakeInput: string;
}

export const EMPTY_DRAFT: DraftSlip = { legs: [], stakeInput: "" };

export function legFromSignal(s: DailyAccaSignal): UserSlipLeg {
  return {
    signal_id: s.signal_id,
    fixture_name: s.fixture_name,
    market: s.market,
    selection: s.selection,
    odds: s.odds,
    league: s.league,
    kickoff_at: s.kickoff_at,
    tier: s.tier,
  };
}

/** Multiplicative combined odds. Returns 1 for empty list (identity). */
export function combinedOdds(legs: readonly UserSlipLeg[]): number {
  let total = 1;
  for (const l of legs) {
    total *= l.odds;
  }
  return total;
}

export function addLeg(draft: DraftSlip, leg: UserSlipLeg): DraftSlip {
  if (draft.legs.some((l) => l.signal_id === leg.signal_id)) return draft;
  return { ...draft, legs: [...draft.legs, leg] };
}

export function removeLeg(draft: DraftSlip, signal_id: string): DraftSlip {
  return { ...draft, legs: draft.legs.filter((l) => l.signal_id !== signal_id) };
}

export function setStakeInput(draft: DraftSlip, value: string): DraftSlip {
  return { ...draft, stakeInput: value };
}

/** Parse the stake input into a positive finite number, else null. */
export function parseStake(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** Compute the potential return from a user-supplied stake. Never invents one. */
export function potentialReturn(legs: readonly UserSlipLeg[], stake: number | null): number | null {
  if (stake === null) return null;
  if (legs.length === 0) return null;
  const odds = combinedOdds(legs);
  return Number((odds * stake).toFixed(2));
}

export function loadDraft(read: (k: string) => string | null): DraftSlip {
  try {
    const raw = read(DRAFT_SLIP_KEY);
    if (!raw) return EMPTY_DRAFT;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return EMPTY_DRAFT;
    const obj = parsed as Record<string, unknown>;
    const legsRaw = Array.isArray(obj["legs"]) ? (obj["legs"] as unknown[]) : [];
    const legs: UserSlipLeg[] = [];
    for (const l of legsRaw) {
      if (!l || typeof l !== "object") continue;
      const r = l as Record<string, unknown>;
      const oddsRaw = r["odds"];
      const odds =
        typeof oddsRaw === "number"
          ? oddsRaw
          : typeof oddsRaw === "string"
            ? Number(oddsRaw)
            : NaN;
      if (
        typeof r["signal_id"] !== "string" ||
        typeof r["fixture_name"] !== "string" ||
        typeof r["market"] !== "string" ||
        typeof r["selection"] !== "string" ||
        typeof r["kickoff_at"] !== "string" ||
        !Number.isFinite(odds) ||
        (r["tier"] !== "tier_a" && r["tier"] !== "tier_b" && r["tier"] !== "tier_c")
      ) {
        continue;
      }
      legs.push({
        signal_id: r["signal_id"] as string,
        fixture_name: r["fixture_name"] as string,
        market: r["market"] as string,
        selection: r["selection"] as string,
        odds,
        league: typeof r["league"] === "string" ? r["league"] : null,
        kickoff_at: r["kickoff_at"] as string,
        tier: r["tier"] as UserSlipLeg["tier"],
      });
    }
    const stakeInput = typeof obj["stakeInput"] === "string" ? obj["stakeInput"] : "";
    return { legs, stakeInput };
  } catch {
    return EMPTY_DRAFT;
  }
}

export function saveDraft(
  draft: DraftSlip,
  write: (k: string, v: string) => void,
): void {
  try {
    write(DRAFT_SLIP_KEY, JSON.stringify(draft));
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function clearDraft(remove: (k: string) => void): void {
  try {
    remove(DRAFT_SLIP_KEY);
  } catch {
    /* ignore */
  }
}
